<template lang="html">
  <div
    class="combo-box"
    :class="{ 'combo-box--open': isOpen }"
    @keydown.down.prevent="moveHighlight(1)"
    @keydown.up.prevent="moveHighlight(-1)"
    @keydown.enter.prevent="selectHighlighted"
    @keydown.esc="close"
  >
    <input
      ref="input"
      v-model="query"
      type="text"
      class="combo-box-input"
      :placeholder="placeholder"
      autocomplete="off"
      @focus="open"
      @input="onInput"
      @blur="onBlur"
    />

    <ul
      v-if="isOpen && (filteredOptions.length > 0 || showAddOption)"
      class="combo-box-menu"
    >
      <li
        v-for="(option, index) in filteredOptions"
        :key="option"
        class="combo-box-option"
        :class="{ 'combo-box-option--highlighted': index === highlightedIndex }"
        @mousedown.prevent="select(option)"
        @mouseenter="highlightedIndex = index"
      >
        {{ option }}
      </li>
      <li
        v-if="showAddOption"
        class="combo-box-option combo-box-option--add"
        :class="{ 'combo-box-option--highlighted': highlightedIndex === filteredOptions.length }"
        @mousedown.prevent="select(query)"
        @mouseenter="highlightedIndex = filteredOptions.length"
      >
        <i class="icon add fitted" /> Use “{{ query.trim() }}”
      </li>
    </ul>
  </div>
</template>

<script>
// A minimal, dependency-free searchable combobox: free-text input + a
// filtered suggestion list. Built to replace Fomantic's jQuery `.dropdown()`
// for cases where the field's value can be driven programmatically (e.g. an
// impressions suggestion pre-filling the row) — jQuery dropdowns keep their
// own internal DOM state that has to be manually re-synced (`dropdown("set
// selected", ...)`) whenever that happens, and any path that misses the
// resync leaves the displayed text out of step with the real value. Here the
// input is a plain v-model, so there's only ever one source of truth.
export default {
  props: {
    value: {
      type: String,
      default: "",
    },
    options: {
      type: Array,
      default: function () {
        return [];
      },
    },
    placeholder: {
      type: String,
      default: "",
    },
    // When false, blurring with text that doesn't exactly match one of
    // `options` reverts to the last confirmed value — mirrors Fomantic's
    // `allowAdditions: false` (used where free-text entries aren't valid,
    // e.g. the Instagram Stories quick-stat panel).
    allowCustom: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      query: this.value || "",
      isOpen: false,
      highlightedIndex: -1,
    };
  },
  computed: {
    filteredOptions() {
      const q = (this.query || "").trim().toLowerCase();
      const matches = q
        ? this.options.filter((option) => option.toLowerCase().includes(q))
        : this.options;
      return matches.slice(0, 50);
    },
    // Explicit "Use '<query>'" affordance so it's obvious this field takes
    // free text, not just a fixed pick-list — otherwise a list of
    // suggestions reads as a closed dropdown.
    showAddOption() {
      const q = (this.query || "").trim();
      if (!q || !this.allowCustom) return false;
      return !this.options.some((option) => option.toLowerCase() === q.toLowerCase());
    },
  },
  watch: {
    value(newVal) {
      if (newVal !== this.query) {
        this.query = newVal || "";
      }
    },
  },
  methods: {
    open() {
      this.isOpen = true;
      this.highlightedIndex = -1;
    },
    close() {
      this.isOpen = false;
      this.highlightedIndex = -1;
    },
    onInput() {
      // Only ever updates local `query` (already done by v-model) to refilter
      // the suggestion list — deliberately does NOT emit to the parent on
      // every keystroke. The parent's v-model target here is often a
      // computed setter with real side effects (e.g. statisticField.vue's
      // metricTypeLookup, which assigns metric_type/label and can toggle a
      // whole extra field into view) — emitting mid-keystroke made the row's
      // layout flicker/shift while the user was still typing. Commit only at
      // a deliberate end point: selecting a suggestion, or blurring.
      this.isOpen = true;
      this.highlightedIndex = -1;
    },
    select(option) {
      this.query = option;
      this.close();
      this.$emit("input", option);
      this.$emit("blur");
    },
    selectHighlighted() {
      if (this.isOpen && this.highlightedIndex >= 0) {
        if (this.highlightedIndex < this.filteredOptions.length) {
          this.select(this.filteredOptions[this.highlightedIndex]);
        } else if (this.showAddOption) {
          this.select(this.query.trim());
        }
      } else if (this.isOpen && this.showAddOption && this.filteredOptions.length === 0) {
        this.select(this.query.trim());
      } else {
        this.close();
        this.$refs.input.blur();
      }
    },
    moveHighlight(delta) {
      if (!this.isOpen) {
        this.open();
        return;
      }
      const count = this.filteredOptions.length + (this.showAddOption ? 1 : 0);
      if (count === 0) return;
      this.highlightedIndex = (this.highlightedIndex + delta + count) % count;
    },
    onBlur() {
      // A click on a suggestion (or the "Use '<query>'" option) never
      // triggers this — @mousedown.prevent on those <li> elements keeps the
      // input focused through the click, so there's nothing here to race
      // against. Safe to commit synchronously.
      if (!this.allowCustom && !this.options.includes(this.query)) {
        this.query = this.options.includes(this.value) ? this.value : "";
      }
      this.close();
      this.$emit("input", this.query);
      this.$emit("blur");
    },
  },
};
</script>

<style lang="scss" scoped>
.combo-box {
  position: relative;
}

.combo-box-input {
  width: 100% !important;
  border: none !important;
  border-bottom: 1px solid transparent !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0.25rem 0.1rem !important;
  font-size: 0.92rem;
  color: var(--text-primary, #1a202c);
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-bottom-color: #f4806f !important;
  }

  &::placeholder {
    color: var(--text-muted, #a0aec0);
  }
}

.combo-box-menu {
  position: absolute;
  z-index: 50;
  top: 100%;
  left: 0;
  right: 0;
  margin: 0.25rem 0 0;
  padding: 0.3rem 0;
  list-style: none;
  background: var(--bg-surface, #fff);
  border: 1px solid var(--border-light, #eef0f2);
  border-radius: 0.4rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-height: 14rem;
  overflow-y: auto;
}

.combo-box-option {
  padding: 0.4rem 0.75rem;
  font-size: 0.88rem;
  color: var(--text-primary, #1a202c);
  cursor: pointer;

  &--highlighted {
    background: rgba(244, 128, 111, 0.1);
  }

  &--add {
    color: #f4806f;
    font-weight: 600;
    border-top: 1px solid var(--border-light, #eef0f2);

    .icon {
      margin-right: 0.2rem;
    }
  }
}
</style>
